import { useEffect, useState } from "react";
import { API, SUPPORT_EMAIL, SUPPORT_PHONE } from "@/config";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { WhatsAppButton } from "@/components/Cta";
import { ArrowLeft, CheckCircle2 } from "lucide-react";

export default function DeleteAccount() {
  const [values, setValues] = useState({ name: "", phone: "", reason: "" });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Delete my NightBay account — NightBay";
  }, []);

  const set = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    const next = {};
    if (values.name.trim().length < 2) next.name = "Please tell us your name.";
    if (!/^\+?\d{10,15}$/.test(values.phone.replace(/[^\d+]/g, "")))
      next.phone = "Enter a valid 10-digit mobile number";
    setErrors(next);
    if (Object.keys(next).length) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/account-deletion`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: values.name.trim(),
          phone: values.phone.replace(/[^\d+]/g, ""),
          reason: values.reason.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) setDone(true);
      else if (res.status === 400 && typeof data.detail === "string") setErrors({ phone: data.detail });
      else setFormError("Something went wrong. Please email support@nightbay.in instead.");
    } catch {
      setFormError("We couldn't reach our servers. Please email support@nightbay.in instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="App" data-testid="delete-account-page">
      <Header />
      <main className="nb-shell max-w-[820px] pb-20 pt-[120px]">
        <a href="/" className="inline-flex items-center gap-2 text-sm text-ink3 hover:text-brand">
          <ArrowLeft size={15} aria-hidden="true" /> Back to nightbay.in
        </a>
        <h1 className="mt-6 text-4xl sm:text-5xl">Delete my account</h1>
        <p className="mt-6 text-base text-ink2">
          You can ask us to delete your NightBay account and the personal data attached to it. Here's
          exactly what happens.
        </p>

        <ol className="mt-8 space-y-4">
          {[
            "In the NightBay app: Profile → Account → Delete my account. Or send the form below.",
            "We call your registered number to confirm it's really you.",
            "If you have a booking in progress, it ends under the 15-day notice rule and you pay only for the month you're in.",
            "Within 30 days we delete your profile, vehicle details, photographs, listings and enquiry records.",
            "We keep only invoices and payout records that Indian tax and company law requires us to retain.",
          ].map((line, i) => (
            <li key={line} data-testid={`delete-step-${i}`} className="nb-card">
              <span className="nb-num text-sm">Step {i + 1}</span>
              <p className="mt-2 text-sm text-ink2">{line}</p>
            </li>
          ))}
        </ol>

        <h2 className="nb-h2 mt-14">Request deletion</h2>
        {done ? (
          <div data-testid="delete-success" className="nb-card mt-6 border-ok/50">
            <CheckCircle2 size={26} className="text-ok" aria-hidden="true" />
            <h3 className="mt-4 text-xl">Request received.</h3>
            <p className="mt-3 text-sm text-ink2">
              We'll call you on the number you gave us to confirm, then delete your data within 30
              days. Questions? {SUPPORT_EMAIL} · {SUPPORT_PHONE}
            </p>
          </div>
        ) : (
          <form data-testid="delete-form" onSubmit={onSubmit} noValidate className="nb-card mt-6">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="del-name" className="block text-sm font-medium text-ink2">
                  Your name
                </label>
                <input
                  id="del-name"
                  data-testid="delete-name"
                  className="nb-input mt-2"
                  value={values.name}
                  onChange={set("name")}
                  aria-invalid={!!errors.name}
                />
                {errors.name && (
                  <p data-testid="delete-error-name" className="mt-2 text-sm text-err">
                    {errors.name}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="del-phone" className="block text-sm font-medium text-ink2">
                  Registered mobile number
                </label>
                <input
                  id="del-phone"
                  data-testid="delete-phone"
                  type="tel"
                  className="nb-input mt-2 font-mono"
                  value={values.phone}
                  onChange={set("phone")}
                  aria-invalid={!!errors.phone}
                />
                {errors.phone && (
                  <p data-testid="delete-error-phone" className="mt-2 text-sm text-err">
                    {errors.phone}
                  </p>
                )}
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="del-reason" className="block text-sm font-medium text-ink2">
                  Reason <span className="text-ink3">(optional)</span>
                </label>
                <textarea
                  id="del-reason"
                  data-testid="delete-reason"
                  rows="3"
                  className="nb-input mt-2"
                  value={values.reason}
                  onChange={set("reason")}
                />
              </div>
            </div>
            {formError && (
              <p data-testid="delete-form-error" className="mt-5 rounded-nb1 border border-err/50 bg-err/10 px-4 py-3 text-sm text-ink">
                {formError}
              </p>
            )}
            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                type="submit"
                data-testid="delete-submit"
                disabled={submitting}
                className="nb-btn-primary disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Request deletion"}
              </button>
              <WhatsAppButton label="WhatsApp us instead" testId="delete-whatsapp" />
            </div>
          </form>
        )}
      </main>
      <Footer />
    </div>
  );
}
